import fetchBHApi from "@helper/fetchBHApi";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import useShortFormVideo from "@hooks/useBrightcoveVideo/useShortFormVideo";
import useWindowDimensions from "@hooks/useWindowDimensions";
import type { BHBasePageProps } from "@routes/BHBasePage";
import BHBasePage from "@routes/BHBasePage";
import {
  fetchContext,
  ResponseType,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { PlaylistType } from "@typings/Playlist.d";
import { useCallback, useEffect, useRef, useState } from "react";
import { ClientLoaderFunctionArgs } from "react-router";

import RingkasVideoPage from "./RingkasVideoPage";
import VideoMetaData from "./VideoMetaData";

export function meta() {
  const sectionName = "ringkas";
  return [getMetaData(VideoMetaData(sectionName).metaTags)];
}

export default function RingkasVideos() {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const {
    playing,
    videos,
    setVideos,
    handleOnEndedVideo,
    handleOnClickThumbnail,
  } = useShortFormVideo({ parentRef, limit: 10 });
  const [isMute, setIsMute] = useState<boolean>(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  const { width } = useWindowDimensions();
  // Tailwind's `lg` breakpoint is 992px by default — consider that desktop
  const isDesktop = width >= 992;

  useEffect(() => {
    // Avoid redirecting during initial hydration (width=0) or before videos are loaded.
    if (!isDesktop && width > 0 && videos[0]?.id) {
      const qs = new URLSearchParams({ ref: "ringkas" }).toString();
      window.location.href = `/quick-news/video/${videos[0].id}?${qs}`;
    }
  }, [isDesktop, width, videos]);

  const handleLoadMoreVideo = useCallback(async () => {
    if (loadingRef.current || !hasMore) {
      return 0;
    }

    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const size = 10;
      const moreVideoApi = `latest-videos-playlist?playlistId=${PlaylistType.SHORT_FORM}&size=${size}&offset=${videos.length}`;
      const moreVideos = await fetchBHApi<LatestVideoResponse[]>(
        moreVideoApi,
        "GET",
      );
      setVideos((prevVideos) => [...prevVideos, ...moreVideos]);
      setHasMore(moreVideos.length === size);

      // This avoids GTM push.
      return 0;
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [videos, hasMore, setVideos]);

  useEffect(() => {
    if (videos.length === 0 || currentSlide === 0) return;
    if (currentSlide >= videos.length - 3 && hasMore && !isLoadingMore) {
      void handleLoadMoreVideo();
    }
  }, [
    currentSlide,
    videos.length,
    hasMore,
    isLoadingMore,
    handleLoadMoreVideo,
  ]);

  const sectionName = "ringkas";
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideScrollableSectionMenu: true,
    hideNavigationInMobile: true,
  };

  const renderVideoPage = () => {
    return (
      <div ref={parentRef} className="w-full">
        <RingkasVideoPage
          playing={playing}
          videos={videos}
          sectionName={sectionName}
          handleOnClickThumbnail={handleOnClickThumbnail}
          handleOnEndedVideo={handleOnEndedVideo}
          onSlideChange={setCurrentSlide}
          isMute={isMute}
          setIsMute={setIsMute}
        />
      </div>
    );
  };

  return <BHBasePage {...basePageProps}>{renderVideoPage()}</BHBasePage>;
}

export function loader(): Promise<TRouteWithRedirect<object | null, string>> {
  const sectionName = "ringkas";
  const { gaData } = VideoMetaData(sectionName);

  return Promise.resolve({
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: null,
    gtm: getGTMData(gaData),
  });
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occurred during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<LatestVideoResponse | null, string>
  >;
}
